<script lang="ts">
    import { onMount } from "svelte";
    import { apiClient } from "./apiClient";
    import { tables, loading, error, success } from "./stores";
    import type { Table } from "./apiClient";
    import QRCodeModal from "./QRCodeModal.svelte";

    let showForm = false;
    let newTableName = "";
    let editingTableId: number | null = null;
    let qrModalOpen = false;
    let selectedTableForQR: Table | null = null;

    onMount(async () => {
        await loadTables();
    });

    async function loadTables() {
        try {
            loading.set(true);
            error.set(null);

            const tablesData = await apiClient.getTables();
            tables.set(tablesData);
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to load tables",
            );
        } finally {
            loading.set(false);
        }
    }

    async function addTable() {
        if (!newTableName.trim()) {
            error.set("Table name is required");
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.createTable({ name: newTableName.trim() });
            newTableName = "";
            showForm = false;
            await loadTables();
            success.set("Table added successfully");
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to add table",
            );
        } finally {
            loading.set(false);
        }
    }

    async function deleteTable(table: Table) {
        if (
            !confirm(`Are you sure you want to delete table "${table.name}"?`)
        ) {
            return;
        }

        try {
            loading.set(true);
            error.set(null);

            await apiClient.deleteTable(table.name);
            await loadTables();
            success.set("Table deleted successfully");
        } catch (err) {
            error.set(
                err instanceof Error ? err.message : "Failed to delete table",
            );
        } finally {
            loading.set(false);
        }
    }

    function toggleForm() {
        showForm = !showForm;
        if (!showForm) {
            newTableName = "";
            editingTableId = null;
        } else {
            editingTableId = -1; // Special ID for new table
        }
    }

    function showQRCode(table: Table) {
        selectedTableForQR = table;
        qrModalOpen = true;
    }

    function closeQRModal() {
        qrModalOpen = false;
        selectedTableForQR = null;
    }
</script>

<div class="section">
    <h2>Tables Management</h2>

    <button class="btn btn-primary" on:click={toggleForm}>
        {showForm ? "Cancel" : "Add New Table"}
    </button>

    {#if showForm && editingTableId === -1}
        <div class="inline-form">
            <div class="form-group">
                <label for="table-name">Table Name</label>
                <input
                    id="table-name"
                    type="text"
                    bind:value={newTableName}
                    placeholder="Enter table name (e.g., Table 1, A1, etc.)"
                    on:keydown={(e) => e.key === "Enter" && addTable()}
                />
                <button
                    class="btn btn-success"
                    on:click={addTable}
                    disabled={$loading}
                >
                    {$loading ? "Adding..." : "Add Table"}
                </button>
            </div>
        </div>
    {/if}

    {#if $loading}
        <div class="loading">Loading...</div>
    {/if}

    {#if $tables.length > 0}
        <table class="table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Actions</th>
                </tr>
            </thead>
            <tbody>
                {#each $tables as table}
                    <tr>
                        <td data-label="Name">{table.name}</td>
                        <td data-label="Actions" class="actions">
                            <button
                                class="btn btn-info"
                                on:click={() => showQRCode(table)}
                                disabled={$loading}
                            >
                                QR Code
                            </button>
                            <button
                                class="btn btn-danger"
                                on:click={() => deleteTable(table)}
                                disabled={$loading}
                            >
                                Delete
                            </button>
                        </td>
                    </tr>
                {/each}
            </tbody>
        </table>
    {:else if !$loading}
        <p>No tables found. Add your first table!</p>
    {/if}
</div>

<QRCodeModal
    isOpen={qrModalOpen}
    table={selectedTableForQR}
    onClose={closeQRModal}
/>

<style>
    .inline-form {
        padding: 20px;
        border: 2px solid #e9ecef;
        border-radius: 8px;
        background-color: #fff;
        margin: 10px 0;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .actions {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .actions .btn {
        margin-right: 0;
        margin-bottom: 0;
    }
</style>
